import { effect, inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { urlJoin } from '../../helpers/url.helper';
import { NOT_COLORS_THEME_KEYS, Palette, PaletteColors, Theming } from './theming.model';
import { Observable, tap } from 'rxjs';
import { AppConfig } from '../../../app.config';
import { ApplicationStore } from '../application-store.service';

@Injectable({
  providedIn: 'root'
})
export class ThemingService {
  private readonly httpClient = inject(HttpClient);
  private readonly appConfig = inject(AppConfig);
  private readonly applicationStore = inject(ApplicationStore);

  private readonly url = urlJoin(this.appConfig.url, 'partners/theme/point-of-sale-code');
  private readonly EMPTY_THEMING: Theming = {
    brand: '',
    logoBytes: '',
    primaryColor: '',
    secondaryColor: '',
    logoFileName: '',
    isThemingSet: false,
    colorPalette: {} as Palette
  };
  private readonly currentTheme = signal<Theming>(this.EMPTY_THEMING);

  constructor() {
    effect(() => {
      const theming = this.currentTheme();
      this.applicationStore.update('logoData', {
        logoFileName: theming.logoFileName,
        logoBytes: theming.logoBytes,
        brand: theming.brand
      });
      document.documentElement.style.setProperty('--pvmo-primary-color', theming.primaryColor);
      document.documentElement.style.setProperty('--pvmo-secondary-color', theming.secondaryColor);

      if (theming.colorPalette) {
        document.documentElement.style.setProperty('--theme-dark-text-color', theming.colorPalette.darkTextColor);
        document.documentElement.style.setProperty('--theme-light-text-color', theming.colorPalette.lightTextColor);
        for (const key of Object.keys(theming.colorPalette).filter(
          colorPaletteKey => !(NOT_COLORS_THEME_KEYS as string[]).includes(colorPaletteKey)
        )) {
          const key1 = `--ml-theme-primary-${key}`;
          const value1 = theming.colorPalette[key as keyof PaletteColors];
          const key2 = `--ml-theme-primary-contrast-${key}`;
          const value2 = theming.colorPalette.useDarkTextColor.includes(key)
            ? theming.colorPalette.darkTextColor
            : theming.colorPalette.lightTextColor;
          document.documentElement.style.setProperty(key1, value1);
          document.documentElement.style.setProperty(key2, value2);
          if (key === '100') {
            document.documentElement.style.setProperty('--ml-theme-primary-border', value1 + '99');
          }
        }
      }
    });
  }

  getThemeFromPoS(pos: string): Observable<Theming> {
    return this.httpClient.get<Theming>(urlJoin(this.url, pos)).pipe(
      tap(theming => {
        this.setCurrentTheming(theming);
      })
    );
  }

  setCurrentTheming(theming: Theming): void {
    theming.isThemingSet = true;
    this.currentTheme.set(theming);
  }
}
